import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateEventPayload,
  EditEventPayload,
} from "../repositories/eventsRepository";
import { eventsRepository } from "../repositories/eventsRepository";

/**
 * Mutation key helpers for event templates
 * Enhanced for better TanStack Query v5 devtools visibility
 */
export const eventTemplateMutationKeys = {
  create: ["event", "template", "create"] as const,
  update: (id?: string) => ["event", "template", "update", id] as const,
  delete: (id?: string) => ["event", "template", "delete", id] as const,
};

// Legacy key helper for backward compatibility
export const eventTemplateMutationKey = (id?: string) =>
  id ? ["event", "template", id] : ["event", "template"];

/**
 * Mutation hook to create a new event template
 */
export function useCreateEventTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: eventTemplateMutationKeys.create,
    mutationFn: (payload: CreateEventPayload) =>
      eventsRepository.createEventTemplate(payload),
    onSuccess: () => {
      // Invalidate event template list queries
      queryClient.invalidateQueries({ queryKey: ["event", "template"] });
    },
    retry: 0,
  });
}

/**
 * Mutation hook to update an existing event template
 */
export function useUpdateEventTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: eventTemplateMutationKeys.update(),
    mutationFn: (payload: EditEventPayload) =>
      eventsRepository.updateEventTemplate(payload),
    onSuccess: (_data, variables) => {
      // Invalidate the specific template and the list
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ["event", "template", "detail", variables.id],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["event", "template"] });
    },
    retry: 0,
  });
}
